import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Search, GraduationCap, Loader2, Edit, X, UserPlus, UserMinus } from 'lucide-react';

const GroupsPage = () => {
    const { getAuthHeaders, API, isAdmin } = useAuth();
    const [groups, setGroups] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState('1');
    const [searchQuery, setSearchQuery] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [editingGroup, setEditingGroup] = useState(null);
    const [editName, setEditName] = useState('');
    const [saving, setSaving] = useState(false);
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignGroupId, setAssignGroupId] = useState(null);
    const [assignSearch, setAssignSearch] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [groupsRes, studentsRes] = await Promise.all([
                axios.get(`${API}/global-groups?year=${selectedYear}`, { headers: getAuthHeaders() }),
                axios.get(`${API}/students?year=${selectedYear}`, { headers: getAuthHeaders() })
            ]);
            setGroups(groupsRes.data);
            setStudents(studentsRes.data);
        } catch (error) {
            toast.error('Failed to load groups');
        } finally {
            setLoading(false);
        }
    }, [API, getAuthHeaders, selectedYear]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const createGroup = async () => {
        if (!newGroupName.trim()) return;
        setSaving(true);
        try {
            await axios.post(`${API}/global-groups`, { name: newGroupName.trim(), year: parseInt(selectedYear) }, { headers: getAuthHeaders() });
            toast.success('Group created');
            setCreateOpen(false);
            setNewGroupName('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create group');
        } finally {
            setSaving(false);
        }
    };

    const updateGroup = async () => {
        if (!editName.trim() || !editingGroup) return;
        setSaving(true);
        try {
            await axios.put(`${API}/global-groups/${editingGroup.id}`, { name: editName.trim() }, { headers: getAuthHeaders() });
            toast.success('Group updated');
            setEditingGroup(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update group');
        } finally {
            setSaving(false);
        }
    };

    const deleteGroup = async (groupId) => {
        try {
            await axios.delete(`${API}/global-groups/${groupId}`, { headers: getAuthHeaders() });
            toast.success('Group deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete group');
        }
    };

    const addStudentToGroup = async (groupId, studentId) => {
        try {
            await axios.post(`${API}/global-groups/${groupId}/add-student/${studentId}`, {}, { headers: getAuthHeaders() });
            toast.success('Student added to group');
            fetchData();
        } catch (error) {
            toast.error('Failed to add student');
        }
    };

    const removeStudentFromGroup = async (groupId, studentId) => {
        try {
            await axios.delete(`${API}/global-groups/${groupId}/remove-student/${studentId}`, { headers: getAuthHeaders() });
            toast.success('Student removed');
            fetchData();
        } catch (error) {
            toast.error('Failed to remove student');
        }
    };

    // Get all student IDs across all groups for this year
    const assignedStudentIds = new Set(groups.flatMap(g => g.student_ids || []));
    const unassignedStudents = students.filter(s => !assignedStudentIds.has(s.id));

    // For assign modal
    const assignGroup = groups.find(g => g.id === assignGroupId);
    const assignableStudents = students
        .filter(s => !(assignGroup?.student_ids || []).includes(s.id))
        .filter(s => !assignSearch || s.name.toLowerCase().includes(assignSearch.toLowerCase()) || s.student_id.toLowerCase().includes(assignSearch.toLowerCase()));

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="groups-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold text-ica-navy">Groups</h1>
                        <p className="text-gray-500 mt-1">Manage student groups — auto-assigned to lessons & assessments</p>
                    </div>
                    {isAdmin() && (
                        <Button onClick={() => setCreateOpen(true)} className="bg-ica-gold hover:bg-[#a8803a]" data-testid="create-group-btn">
                            <Plus className="w-4 h-4 mr-2" /> New Group
                        </Button>
                    )}
                </div>

                {/* Year filter */}
                <div className="flex items-center gap-4">
                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                        <SelectTrigger className="w-[180px]" data-testid="groups-year-filter">
                            <SelectValue placeholder="Select Year" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">Year 1</SelectItem>
                            <SelectItem value="2">Year 2</SelectItem>
                            <SelectItem value="3">Year 3</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="text-sm text-gray-500">{groups.length} group(s) • {students.length} students in Year {selectedYear}</div>
                </div>

                {/* Unassigned students badge */}
                {unassignedStudents.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2" data-testid="unassigned-banner">
                        <GraduationCap className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <span className="text-sm text-amber-800">
                            <strong>{unassignedStudents.length}</strong> student(s) not assigned to any group:
                            {' '}{unassignedStudents.slice(0, 5).map(s => s.name).join(', ')}
                            {unassignedStudents.length > 5 && ` + ${unassignedStudents.length - 5} more`}
                        </span>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-16 bg-white rounded-xl border"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy mx-auto" /></div>
                ) : groups.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
                        <Users className="w-10 h-10 mx-auto mb-3 text-gray-400 opacity-40" />
                        <h3 className="text-base font-semibold text-gray-700 mb-1">No groups yet</h3>
                        <p className="text-gray-500 mb-4">Create groups to organize Year {selectedYear} students</p>
                        {isAdmin() && <Button onClick={() => setCreateOpen(true)} className="bg-ica-gold hover:bg-[#a8803a]"><Plus className="w-4 h-4 mr-2" />Create Group</Button>}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {groups.map(group => {
                            const groupStudents = students.filter(s => (group.student_ids || []).includes(s.id));
                            const isExpanded = expandedGroup === group.id;
                            return (
                                <div key={group.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-testid={`group-card-${group.id}`}>
                                    {/* Group header */}
                                    <div className="p-4 border-b border-gray-100">
                                        <div className="flex items-center justify-between mb-1">
                                            <h3 className="font-semibold text-gray-900">{group.name}</h3>
                                            {isAdmin() && (
                                                <div className="flex items-center gap-1">
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold" onClick={() => { setEditingGroup(group); setEditName(group.name); }} data-testid={`edit-group-${group.id}`} title="Rename Group">
                                                        <Edit className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300" onClick={() => deleteGroup(group.id)} data-testid={`delete-group-${group.id}`} title="Delete Group">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500">Year {group.year} • {groupStudents.length} student(s)</span>
                                            {isAdmin() && (
                                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setAssignGroupId(group.id); setAssignModalOpen(true); setAssignSearch(''); }} data-testid={`assign-students-${group.id}`}>
                                                    <UserPlus className="w-3 h-3 mr-1" /> Add Students
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Students list */}
                                    <div className="divide-y divide-gray-50 max-h-[300px] overflow-y-auto">
                                        {groupStudents.length === 0 ? (
                                            <p className="text-sm text-gray-400 text-center py-6">No students assigned</p>
                                        ) : (
                                            (isExpanded ? groupStudents : groupStudents.slice(0, 5)).map(s => (
                                                <div key={s.id} className="flex items-center justify-between px-4 py-2 hover:bg-gray-50" data-testid={`group-student-${s.id}`}>
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <div className="w-7 h-7 bg-ica-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                                                            <GraduationCap className="w-3.5 h-3.5 text-ica-navy" />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 truncate">{s.name}</p>
                                                            <p className="text-xs text-gray-400">{s.student_id}</p>
                                                        </div>
                                                    </div>
                                                    {isAdmin() && (
                                                        <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300" onClick={() => removeStudentFromGroup(group.id, s.id)} title="Remove from Group">
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                        {!isExpanded && groupStudents.length > 5 && (
                                            <button className="w-full text-center text-xs text-ica-gold font-medium py-2 hover:bg-gray-50" onClick={() => setExpandedGroup(group.id)}>
                                                Show {groupStudents.length - 5} more
                                            </button>
                                        )}
                                        {isExpanded && groupStudents.length > 5 && (
                                            <button className="w-full text-center text-xs text-gray-400 font-medium py-2 hover:bg-gray-50" onClick={() => setExpandedGroup(null)}>
                                                Show less
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Create Group Modal */}
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogContent data-testid="create-group-modal">
                        <DialogTopBar title={<>Create Group</>}>
    <Button size="sm" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                            <Button size="sm" onClick={createGroup} disabled={saving || !newGroupName.trim()}  data-testid="confirm-create-group">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                            </Button>
</DialogTopBar>
                        <div className="space-y-4 py-2">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Group Name</label>
                                <Input value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="e.g. Group A" className="mt-1" data-testid="new-group-name-input"
                                    onKeyDown={e => e.key === 'Enter' && createGroup()} />
                            </div>
                            <p className="text-xs text-gray-500">This group will be created for <strong>Year {selectedYear}</strong></p>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Edit Group Modal */}
                <Dialog open={!!editingGroup} onOpenChange={() => setEditingGroup(null)}>
                    <DialogContent>
                        <DialogTopBar title={<>Rename Group</>}>
    <Button size="sm" variant="outline" onClick={() => setEditingGroup(null)}>Cancel</Button>
                            <Button size="sm" onClick={updateGroup} disabled={saving || !editName.trim()}  data-testid="confirm-edit-group">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                            </Button>
</DialogTopBar>
                        <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Group name" data-testid="edit-group-name-input"
                            onKeyDown={e => e.key === 'Enter' && updateGroup()} />
                    </DialogContent>
                </Dialog>

                {/* Assign Students Modal */}
                <Dialog open={assignModalOpen} onOpenChange={setAssignModalOpen}>
                    <DialogContent className="max-w-lg max-h-[80vh]" data-testid="assign-students-modal">
                        <DialogHeader><DialogTitle>Add Students to {assignGroup?.name}</DialogTitle></DialogHeader>
                        <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input value={assignSearch} onChange={e => setAssignSearch(e.target.value)} placeholder="Search students..." className="pl-9" data-testid="assign-search-input" />
                        </div>
                        <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto border rounded-lg">
                            {assignableStudents.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-8">No students available to add</p>
                            ) : (
                                assignableStudents.map(s => {
                                    const currentGroup = groups.find(g => (g.student_ids || []).includes(s.id));
                                    return (
                                        <div key={s.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50" data-testid={`assignable-student-${s.id}`}>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{s.name}</p>
                                                <p className="text-xs text-gray-400">{s.student_id}
                                                    {currentGroup && <span className="ml-1 text-amber-600">• Currently in {currentGroup.name}</span>}
                                                </p>
                                            </div>
                                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => addStudentToGroup(assignGroupId, s.id)} data-testid={`add-to-group-${s.id}`}>
                                                <UserPlus className="w-3 h-3 mr-1" /> Add
                                            </Button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};

export default GroupsPage;
